import { fabricGroupingKey, srDisplayLabel } from "./fabricTextEditing";

const toText = (value) => String(value ?? "").trim();

const toNumberOrNull = (value) => {
  if (value == null || String(value).trim() === "") return null;
  const numeric = Number(String(value ?? "").replace(/,/g, "").trim());
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : null;
};

export const normalizeFabricWidthIn = (value, fallback = null) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0 && numeric <= 300) {
    return Number(numeric.toFixed(2));
  }
  return fallback;
};

export const canChangeCataloguePriceUnit = (catalogue, nextUnit, rules = []) =>
  nextUnit === (catalogue.priceUnit || "m") ||
  (catalogue.defaultRrpPerMeter == null && !rules.some(rule =>
    rule.active !== false && rule.archived !== true && rule.rrpPerMeter != null));

const escapeRegExp = (value = "") =>
  String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const normalizeCatalogueKey = (name = "") =>
  toText(name)
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

// Exact identifiers are not numbers or search tokens. Only trim at commit.
export const normalizeSrNumber = (sr = "") => toText(sr);

const srMatchKey = value => toText(value).replace(/^S\.?\s*R\.?\s*(?:NO\.?|NUMBER)?\s*[:#-]\s*/i, "").toLowerCase();
export const getSrPricingMatchKey = srMatchKey;

export const normalizeSrList = (value) =>
  (Array.isArray(value) ? value : String(value || "").split(/[,;\n]+/))
    .map((entry) => normalizeSrNumber(entry))
    .filter(Boolean)
    .filter((entry, index, list) => list.indexOf(entry) === index);

export const numericSrValue = (value) => {
  const match = srMatchKey(value).match(/^\d+$/);
  return match ? Number(match[0]) : null;
};

const stripSrPattern = (text = "") => {
  const match = String(text || "").match(
    /^(.*?)\s+S\.?\s*R\.?\s*(?:NO\.?|NUMBER)?\s*[:#-]?\s*(.+?)\s*$/i
  );
  if (!match) return null;
  return {
    catalogueName: toText(match[1]),
    srNumber: normalizeSrNumber(match[2]),
    confidence: "high",
  };
};

const isSrRuleRangeOrListText = (value = "") => {
  const text = toText(value);
  if (!text) return false;
  return /^(?:SR\s*)?\d+\s+(?:to|[–—])\s*\d+$/i.test(text);
};

export const buildFabricCatalogueDisplayCode = ({
  catalogueName = "",
  srNumber = "",
  fallback = "",
} = {}) => {
  const catalogue = toText(catalogueName);
  const normalizedSr = normalizeSrNumber(srNumber);
  if (catalogue && normalizedSr) return `${catalogue} ${srDisplayLabel(normalizedSr)}`;
  if (catalogue) return catalogue;
  return toText(fallback);
};

export const stripStaleFabricBrandPrefix = (value = "", candidates = []) => {
  let text = toText(value);
  if (!text) return "";
  const brandCandidates = (Array.isArray(candidates) ? candidates : [candidates])
    .map((candidate) => toText(candidate))
    .filter(Boolean)
    .sort((left, right) => right.length - left.length);

  for (const brandName of brandCandidates) {
    const pattern = new RegExp(
      `^${escapeRegExp(brandName)}\\s*(?:/|\\\\|>|:|\\-|\\u2013|\\u2014)\\s*(.+)$`,
      "i"
    );
    const match = text.match(pattern);
    if (match?.[1]) {
      text = toText(match[1]);
      break;
    }
  }

  return text;
};

const sortCataloguesByNameLength = (catalogues = []) =>
  [...catalogues].sort(
    (a, b) =>
      String(b.catalogueName || b.name || "").length -
      String(a.catalogueName || a.name || "").length
  );

export const parseFabricCatalogueInput = (
  rawFabricText = "",
  knownCatalogues = []
) => {
  const raw = toText(rawFabricText);
  const empty = {
    raw,
    catalogueName: "",
    catalogueKey: "",
    srNumber: "",
    srLabel: "",
    confidence: "low",
  };
  if (!raw) return empty;

  const srPattern = stripSrPattern(raw);
  if (srPattern?.catalogueName) {
    return {
      raw,
      catalogueName: srPattern.catalogueName,
      catalogueKey: normalizeCatalogueKey(srPattern.catalogueName),
      srNumber: srPattern.srNumber,
      srLabel: srDisplayLabel(srPattern.srNumber),
      confidence: srPattern.confidence,
    };
  }

  const srOnlyMatch = raw.match(
    /^S\.?\s*R\.?\s*(?:NO\.?|NUMBER)?\s*(?:[:#-]\s*(.+)|(\d+))$/i
  );
  if (srOnlyMatch) {
    const singleKnownCatalogue =
      knownCatalogues.length === 1 ? knownCatalogues[0] : null;
    const catalogueName =
      singleKnownCatalogue?.catalogueName || singleKnownCatalogue?.name || "";
    return {
      raw,
      catalogueName,
      catalogueKey: catalogueName ? normalizeCatalogueKey(catalogueName) : "",
      srNumber: normalizeSrNumber(srOnlyMatch[1] ?? srOnlyMatch[2]),
      srLabel: srDisplayLabel(srOnlyMatch[1] ?? srOnlyMatch[2]),
      confidence: catalogueName ? "medium" : "low",
    };
  }

  const normalizedRawKey = normalizeCatalogueKey(raw);
  const catalogues = sortCataloguesByNameLength(knownCatalogues);
  const exactCatalogue = catalogues.find(
    (catalogue) =>
      normalizeCatalogueKey(catalogue.catalogueName || catalogue.name) ===
      normalizedRawKey
  );
  if (exactCatalogue) {
    const catalogueName =
      exactCatalogue.catalogueName || exactCatalogue.name || raw;
    return {
      raw,
      catalogueName,
      catalogueKey: normalizeCatalogueKey(catalogueName),
      srNumber: "",
      srLabel: "",
      confidence: "high",
    };
  }

  for (const catalogue of catalogues) {
    const catalogueName = catalogue.catalogueName || catalogue.name || "";
    if (!catalogueName) continue;
    const escapedName = catalogueName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const match = raw.match(new RegExp(`^${escapedName}\\s+(.+)$`, "i"));
    if (!match) continue;
    const rawSuffix = toText(match[1]);
    if (isSrRuleRangeOrListText(rawSuffix)) {
      return {
        raw,
        catalogueName,
        catalogueKey: normalizeCatalogueKey(catalogueName),
        srNumber: "",
        srLabel: "",
        confidence: "medium",
      };
    }
    const suffix = normalizeSrNumber(rawSuffix);
    if (!suffix) continue;
    return {
      raw,
      catalogueName,
      catalogueKey: normalizeCatalogueKey(catalogueName),
      srNumber: suffix,
      srLabel: srDisplayLabel(suffix),
      confidence: "medium",
    };
  }

  return {
    raw,
    catalogueName: raw,
    catalogueKey: normalizeCatalogueKey(raw),
    srNumber: "",
    srLabel: "",
    confidence: "low",
  };
};

export const normalizeFabricCatalogue = (
  catalogue = {},
  fallbackId = ""
) => {
  const catalogueName = toText(
    catalogue.catalogueName || catalogue.name || catalogue.displayName || ""
  );
  const catalogueKey =
    catalogue.catalogueKey || normalizeCatalogueKey(catalogueName);
  const defaultFabricWidthIn = normalizeFabricWidthIn(
    catalogue.catalogueDefaultFabricWidthIn ??
      catalogue.defaultFabricWidthIn ??
      catalogue.fabricWidthIn
  );
  return {
    id: toText(catalogue.id || catalogue.catalogueId || fallbackId || catalogueKey),
    brandId: toText(catalogue.brandId || ""),
    brandName: toText(catalogue.brandName || ""),
    catalogueName,
    catalogueKey,
    defaultRrpPerMeter: toNumberOrNull(catalogue.defaultRrpPerMeter),
    priceUnit: catalogue.priceUnit == null ? "m" : String(catalogue.priceUnit),
    defaultFabricWidthIn,
    catalogueDefaultFabricWidthIn: defaultFabricWidthIn,
    currency: catalogue.currency || "INR",
    active: catalogue.active !== false && catalogue.archived !== true,
    archived: catalogue.archived === true || catalogue.active === false,
    notes: toText(catalogue.notes || ""),
    updatedAt: catalogue.updatedAt || null,
    createdAt: catalogue.createdAt || null,
  };
};

export const normalizeSrPriceRule = (rule = {}, fallbackId = "") => {
  const ruleType = ["single", "range", "list", "default"].includes(
    rule.ruleType
  )
    ? rule.ruleType
    : rule.srFrom && rule.srTo
    ? "range"
    : Array.isArray(rule.srNumbers)
    ? "list"
    : rule.srNumber
    ? "single"
    : "default";
  const normalizedSrNumbers =
    ruleType === "list"
      ? normalizeSrList(rule.normalizedSrNumbers || rule.srNumbers)
      : ruleType === "single"
      ? [normalizeSrNumber(rule.srNumber)]
      : [];

  return {
    id: toText(rule.id || rule.ruleId || fallbackId || ""),
    brandId: toText(rule.brandId || ""),
    brandName: toText(rule.brandName || ""),
    catalogueId: toText(rule.catalogueId || ""),
    catalogueName: toText(rule.catalogueName || ""),
    catalogueKey: rule.catalogueKey || normalizeCatalogueKey(rule.catalogueName),
    ruleType,
    srNumber: normalizeSrNumber(rule.srNumber),
    srFrom: normalizeSrNumber(rule.srFrom),
    srTo: normalizeSrNumber(rule.srTo),
    srNumbers: normalizedSrNumbers,
    normalizedSrNumbers,
    rrpPerMeter: toNumberOrNull(rule.rrpPerMeter),
    fabricWidthIn: normalizeFabricWidthIn(rule.fabricWidthIn),
    fabricWidthSource: normalizeFabricWidthIn(rule.fabricWidthIn)
      ? "catalogue_rule"
      : "",
    currency: rule.currency || "INR",
    active: rule.active !== false && rule.archived !== true,
    archived: rule.archived === true || rule.active === false,
    notes: toText(rule.notes || ""),
    updatedAt: rule.updatedAt || null,
    createdAt: rule.createdAt || null,
  };
};

const ruleMatchesSr = (rule = {}, srNumber = "") => {
  const normalizedSr = normalizeSrNumber(srNumber);
  if (!normalizedSr || !rule.active) return false;

  if (rule.ruleType === "single") {
    return srMatchKey(rule.srNumber) === srMatchKey(normalizedSr);
  }

  if (rule.ruleType === "list") {
    return (rule.normalizedSrNumbers || rule.srNumbers || [])
      .map((entry) => srMatchKey(entry))
      .includes(srMatchKey(normalizedSr));
  }

  if (rule.ruleType === "range") {
    const srNumeric = numericSrValue(normalizedSr);
    const fromNumeric = numericSrValue(rule.srFrom);
    const toNumeric = numericSrValue(rule.srTo);
    return (
      srNumeric != null &&
      fromNumeric != null &&
      toNumeric != null &&
      srNumeric >= Math.min(fromNumeric, toNumeric) &&
      srNumeric <= Math.max(fromNumeric, toNumeric)
    );
  }

  return false;
};

const getRulePriority = (rule = {}) => {
  if (rule.ruleType === "single") return 1;
  if (rule.ruleType === "list") return 2;
  if (rule.ruleType === "range") return 3;
  if (rule.ruleType === "default") return 4;
  return 9;
};

const getRuleRangeSpan = (rule = {}) => {
  if (rule.ruleType !== "range") return 0;
  const fromNumeric = numericSrValue(rule.srFrom);
  const toNumeric = numericSrValue(rule.srTo);
  if (fromNumeric == null || toNumeric == null) return Number.MAX_SAFE_INTEGER;
  return Math.abs(toNumeric - fromNumeric);
};

const getRuleUpdatedMillis = (rule = {}) => {
  const value = rule.updatedAt || rule.createdAt || null;
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

export const getSrPriceRuleLabel = (rule = {}) => {
  if (rule.ruleType === "single") return srDisplayLabel(rule.srNumber);
  if (rule.ruleType === "range") {
    return `SR-${normalizeSrNumber(rule.srFrom)} to ${normalizeSrNumber(rule.srTo)}`;
  }
  if (rule.ruleType === "list") {
    return normalizeSrList(rule.normalizedSrNumbers || rule.srNumbers)
      .map((entry) => srDisplayLabel(entry))
      .join(", ");
  }
  return "Catalogue default";
};

const resolveCatalogueFabricWidth = ({
  catalogue = {},
  rule = null,
  workspaceDefaultFabricWidthIn = 54,
} = {}) => {
  const srRuleFabricWidthIn = normalizeFabricWidthIn(rule?.fabricWidthIn);
  if (srRuleFabricWidthIn) {
    return {
      fabricWidthIn: srRuleFabricWidthIn,
      fabricWidthSource: "catalogue_rule",
      fabricWidthManuallyOverridden: false,
      srRuleFabricWidthIn,
      catalogueDefaultFabricWidthIn: normalizeFabricWidthIn(
        catalogue?.catalogueDefaultFabricWidthIn ??
          catalogue?.defaultFabricWidthIn ??
          catalogue?.fabricWidthIn
      ),
    };
  }

  const catalogueDefaultFabricWidthIn = normalizeFabricWidthIn(
    catalogue?.catalogueDefaultFabricWidthIn ??
      catalogue?.defaultFabricWidthIn ??
      catalogue?.fabricWidthIn
  );
  if (catalogueDefaultFabricWidthIn) {
    return {
      fabricWidthIn: catalogueDefaultFabricWidthIn,
      fabricWidthSource: "catalogue_default",
      fabricWidthManuallyOverridden: false,
      srRuleFabricWidthIn: null,
      catalogueDefaultFabricWidthIn,
    };
  }

  const fallbackFabricWidthIn =
    normalizeFabricWidthIn(workspaceDefaultFabricWidthIn) || 54;
  return {
    fabricWidthIn: fallbackFabricWidthIn,
    fabricWidthSource: "workspace_default",
    fabricWidthManuallyOverridden: false,
    srRuleFabricWidthIn: null,
    catalogueDefaultFabricWidthIn: null,
  };
};

export const resolveFabricCatalogueRrp = ({
  brand = {},
  library = {},
  fabricText = "",
  catalogueId = "",
  srNumber = undefined,
  existingMrp = "",
  workspaceDefaultFabricWidthIn = 54,
} = {}) => {
  const catalogues = Array.isArray(library.catalogues) ? library.catalogues : [];
  const rulesByCatalogueId =
    library.rulesByCatalogueId && typeof library.rulesByCatalogueId === "object"
      ? library.rulesByCatalogueId
      : {};
  const selectedCatalogue = catalogueId ? catalogues.find(entry => entry.active && entry.id === catalogueId) : null;
  const parsed = selectedCatalogue && srNumber !== undefined
    ? { raw: fabricText, catalogueName: selectedCatalogue.catalogueName, catalogueKey: selectedCatalogue.catalogueKey,
        srNumber: normalizeSrNumber(srNumber), srLabel: srDisplayLabel(srNumber), confidence: "high" }
    : parseFabricCatalogueInput(fabricText, catalogues);
  const catalogue = selectedCatalogue || catalogues.find(
    (entry) =>
      entry.active &&
      entry.catalogueKey &&
      entry.catalogueKey === parsed.catalogueKey
  );

  const baseResult = {
    parsed,
    catalogue: catalogue || null,
    priceUnit: catalogue?.priceUnit || "m",
    rule: null,
    rrpPerMeter: toNumberOrNull(existingMrp),
    rrpSource: toNumberOrNull(existingMrp) !== null ? "manual_override" : "unmatched",
    fabricWidthIn: null,
    fabricWidthSource: "",
    fabricWidthManuallyOverridden: false,
    srRuleFabricWidthIn: null,
    catalogueDefaultFabricWidthIn: null,
    matched: false,
    brandId: brand?.id || "",
    brandName: brand?.name || "",
  };

  if (!catalogue) return baseResult;

  const activeRules = (rulesByCatalogueId[catalogue.id] || []).filter(
    (rule) => rule.active
  );
  const matchingRules = parsed.srNumber
    ? activeRules.filter((rule) => ruleMatchesSr(rule, parsed.srNumber))
    : [];
  matchingRules.sort((left, right) => {
    const priorityDiff = getRulePriority(left) - getRulePriority(right);
    if (priorityDiff !== 0) return priorityDiff;
    const rangeSpanDiff = getRuleRangeSpan(left) - getRuleRangeSpan(right);
    if (rangeSpanDiff !== 0) return rangeSpanDiff;
    return getRuleUpdatedMillis(right) - getRuleUpdatedMillis(left);
  });
  const matchedRule = matchingRules[0] || null;

  if (matchedRule) {
    const widthResult = resolveCatalogueFabricWidth({
      catalogue,
      rule: matchedRule,
      workspaceDefaultFabricWidthIn,
    });
    return {
      ...baseResult,
      catalogue,
      rule: matchedRule,
      rrpPerMeter:
        matchedRule.rrpPerMeter != null
          ? Number(matchedRule.rrpPerMeter)
          : baseResult.rrpPerMeter,
      rrpSource:
        matchedRule.rrpPerMeter != null ? "sr_price_rule" : baseResult.rrpSource,
      ...widthResult,
      matched: true,
    };
  }

  const defaultRule = activeRules.find((rule) => rule.ruleType === "default");
  if (defaultRule) {
    const widthResult = resolveCatalogueFabricWidth({
      catalogue,
      rule: defaultRule,
      workspaceDefaultFabricWidthIn,
    });
    return {
      ...baseResult,
      catalogue,
      rule: defaultRule,
      rrpPerMeter:
        defaultRule.rrpPerMeter != null
          ? Number(defaultRule.rrpPerMeter)
          : baseResult.rrpPerMeter,
      rrpSource:
        defaultRule.rrpPerMeter != null ? "catalogue_default" : baseResult.rrpSource,
      ...widthResult,
      matched: true,
    };
  }

  if (catalogue.defaultRrpPerMeter != null) {
    const widthResult = resolveCatalogueFabricWidth({
      catalogue,
      workspaceDefaultFabricWidthIn,
    });
    return {
      ...baseResult,
      catalogue,
      rrpPerMeter: Number(catalogue.defaultRrpPerMeter),
      rrpSource: "catalogue_default",
      ...widthResult,
      matched: true,
    };
  }

  const widthResult = resolveCatalogueFabricWidth({
    catalogue,
    workspaceDefaultFabricWidthIn,
  });
  return {
    ...baseResult,
    catalogue,
    ...widthResult,
    rrpSource: toNumberOrNull(existingMrp) !== null ? "manual_override" : "unmatched",
  };
};

export const validateQuoteFabricAgainstSettings = (
  row = {},
  {
    brand = null,
    library = {},
    fabricText = "",
    existingMrp = "",
    workspaceDefaultFabricWidthIn = 54,
  } = {}
) => {
  const typedText =
    toText(fabricText) ||
    toText(
      row.fabricCodeRaw ||
        row.fabricCode ||
        row.fabric ||
        row.fabricName ||
        row.materialCode ||
        row.material ||
        row.catalogueName ||
        ""
    );
  const brandId = toText(
    brand?.id || row.brandId || row.fabricBrandId || ""
  );
  const brandName = toText(
    brand?.name || brand?.brandName || row.brandName || row.fabricBrandName || ""
  );
  const activeBrand =
    brand && brandId && brand.archived !== true && brand.active !== false
      ? brand
      : null;
  const catalogues = Array.isArray(library.catalogues)
    ? library.catalogues.filter((catalogue) => catalogue?.active)
    : [];
  const parsed = parseFabricCatalogueInput(typedText, catalogues);
  const requestedCatalogueId = toText(row.catalogueId || "");
  const requestedCatalogueKey = toText(
    row.catalogueKey || normalizeCatalogueKey(row.catalogueName || "")
  );
  const requestedCatalogueName = toText(row.catalogueName || "");
  const parsedCatalogueKey = parsed.catalogueKey || "";
  const catalogue = activeBrand
    ? catalogues.find((entry) => {
        const entryKey = normalizeCatalogueKey(
          entry.catalogueKey || entry.catalogueName || entry.name || ""
        );
        const entryNameKey = normalizeCatalogueKey(entry.catalogueName || entry.name || "");
        return (
          (requestedCatalogueId && entry.id === requestedCatalogueId) ||
          (requestedCatalogueKey && entryKey === normalizeCatalogueKey(requestedCatalogueKey)) ||
          (requestedCatalogueName &&
            entryNameKey === normalizeCatalogueKey(requestedCatalogueName)) ||
          (parsedCatalogueKey && entryKey === parsedCatalogueKey)
        );
      }) || null
    : null;
  const validSrNumber = normalizeSrNumber(
    row.srNumber || row.srNo || row.sr || parsed.srNumber || ""
  );
  const resolutionFabricText = catalogue
    ? buildFabricCatalogueDisplayCode({
        catalogueName: catalogue.catalogueName || requestedCatalogueName || typedText,
        srNumber: validSrNumber,
        fallback: typedText,
      })
    : typedText;
  const resolution =
    activeBrand && catalogue
      ? resolveFabricCatalogueRrp({
          brand: activeBrand,
          library,
          fabricText: resolutionFabricText,
          existingMrp,
          workspaceDefaultFabricWidthIn,
        })
      : null;
  const rawDisplayFabricCode = catalogue
    ? buildFabricCatalogueDisplayCode({
        catalogueName: catalogue.catalogueName,
        srNumber: validSrNumber,
        fallback: typedText,
      })
    : typedText;
  const displayFabricCode = catalogue
    ? rawDisplayFabricCode
    : stripStaleFabricBrandPrefix(rawDisplayFabricCode, [
        brandName,
        row.brandName,
        row.fabricBrandName,
        brand?.name,
        brand?.brandName,
      ]);
  const invalidReason = !typedText
    ? "empty_fabric_code"
    : !activeBrand
    ? brandId || brandName
      ? "brand_not_found_or_archived"
      : "no_brand_selected"
    : !catalogues.length
    ? "catalogue_not_loaded_or_empty"
    : requestedCatalogueId || requestedCatalogueKey || requestedCatalogueName || parsedCatalogueKey
    ? "catalogue_not_found_or_archived"
    : "custom_not_in_settings";

  if (!activeBrand || !catalogue) {
    return {
      isValidCatalogue: false,
      validBrandId: "",
      validBrandName: "",
      validCatalogueId: "",
      validCatalogueName: "",
      validCatalogueKey: "",
      displayFabricCode,
      fabricSource: typedText ? "quote_manual" : "none",
      invalidReason,
      parsed,
      resolution: null,
      rrpPerMeter: toNumberOrNull(existingMrp),
      rrpSource: toNumberOrNull(existingMrp) !== null ? "manual_quote" : "quote_manual",
      fabricWidthIn: normalizeFabricWidthIn(row.fabricWidthIn),
      fabricWidthSource: row.fabricWidthSource || "",
      fabricWidthManuallyOverridden: row.fabricWidthManuallyOverridden === true,
      catalogueDefaultFabricWidthIn: normalizeFabricWidthIn(row.catalogueDefaultFabricWidthIn),
      srRuleFabricWidthIn: normalizeFabricWidthIn(row.srRuleFabricWidthIn),
    };
  }

  return {
    isValidCatalogue: true,
    validBrandId: activeBrand.id || brandId,
    validBrandName: activeBrand.name || brandName,
    validCatalogueId: catalogue.id || "",
    validCatalogueName: catalogue.catalogueName || "",
    validCatalogueKey: catalogue.catalogueKey || normalizeCatalogueKey(catalogue.catalogueName),
    displayFabricCode,
    fabricSource: "settings_catalogue",
    invalidReason: "",
    parsed,
    resolution,
    rrpPerMeter: resolution?.rrpPerMeter ?? toNumberOrNull(existingMrp),
    rrpSource: resolution?.rrpSource || (toNumberOrNull(existingMrp) !== null ? "manual_override" : "unmatched"),
    fabricWidthIn: resolution?.fabricWidthIn ?? normalizeFabricWidthIn(row.fabricWidthIn),
    fabricWidthSource: resolution?.fabricWidthSource || row.fabricWidthSource || "",
    fabricWidthManuallyOverridden: row.fabricWidthManuallyOverridden === true,
    catalogueDefaultFabricWidthIn:
      resolution?.catalogueDefaultFabricWidthIn ??
      normalizeFabricWidthIn(row.catalogueDefaultFabricWidthIn),
    srRuleFabricWidthIn:
      resolution?.srRuleFabricWidthIn ?? normalizeFabricWidthIn(row.srRuleFabricWidthIn),
  };
};

export const sanitizeFabricForDisplayAgainstSettings = (
  row = {},
  {
    brand = null,
    library = {},
    fabricText = "",
    existingMrp = "",
    workspaceDefaultFabricWidthIn = 54,
  } = {}
) => {
  const validation = validateQuoteFabricAgainstSettings(row, {
    brand,
    library,
    fabricText,
    existingMrp,
    workspaceDefaultFabricWidthIn,
  });

  if (validation.isValidCatalogue) {
    return {
      displayBrandName: validation.validBrandName || "",
      displayCatalogueName: validation.validCatalogueName || "",
      displayFabricCode: validation.displayFabricCode || "",
      source: "settings_catalogue",
      isValidCatalogue: true,
      invalidReason: "",
      validation,
    };
  }

  return {
    displayBrandName: "",
    displayCatalogueName: "",
    displayFabricCode:
      validation.displayFabricCode ||
      stripStaleFabricBrandPrefix(fabricText || "", [
        row.brandName,
        row.fabricBrandName,
        brand?.name,
        brand?.brandName,
      ]),
    source: validation.fabricSource || "quote_manual",
    isValidCatalogue: false,
    invalidReason: validation.invalidReason || "catalogue_not_found_in_settings",
    validation,
  };
};

export const buildFabricCatalogueRowPatch = (resolution = {}) => {
  const parsed = resolution.parsed || {};
  const catalogue = resolution.catalogue || {};
  const hasValidCatalogue = Boolean(catalogue.id && catalogue.catalogueName);
  const rule = resolution.rule || {};
  // Keep persisted fabric strings compatible with existing grouping/repeat keys.
  const srNumber = normalizeSrNumber(parsed.srNumber);
  const storedLabel = srDisplayLabel(srNumber);
  const fabricCode = hasValidCatalogue
    ? `${toText(catalogue.catalogueName)}${srNumber ? ` ${storedLabel}` : ""}`
    : toText(parsed.raw);
  const ruleLabel = rule.id ? getSrPriceRuleLabel(rule) : "";
  return {
    fabric: fabricCode,
    fabricName: fabricCode,
    fabricCode,
    fabricCodeRaw: fabricCode,
    catalogueId: hasValidCatalogue ? catalogue.id || "" : "",
    catalogueName: hasValidCatalogue ? catalogue.catalogueName || "" : "",
    catalogueKey: hasValidCatalogue ? catalogue.catalogueKey || "" : "",
    srNumber: parsed.srNumber || "",
    srLabel: parsed.srLabel || "",
    srPriceRuleId: rule.id || "",
    srPriceRuleType: rule.ruleType || (resolution.rrpSource === "catalogue_default" ? "default" : "none"),
    srPriceRuleLabel: ruleLabel,
    rrpPerMeter: resolution.rrpPerMeter ?? null,
    rrpSource: resolution.rrpSource || "unmatched",
    fabricWidthIn: resolution.fabricWidthIn || "",
    fabricWidthSource: resolution.fabricWidthSource || "",
    fabricWidthManuallyOverridden:
      resolution.fabricWidthManuallyOverridden === true ? true : false,
    catalogueDefaultFabricWidthIn: resolution.catalogueDefaultFabricWidthIn || null,
    srRuleFabricWidthIn: resolution.srRuleFabricWidthIn || null,
  };
};

export const buildSrPriceRuleDocId = (draft = {}) => {
  const ruleType = draft.ruleType || "single";
  if (ruleType === "default") return "default";
  if (ruleType === "range") {
    return `range-${normalizeCatalogueKey(`${draft.srFrom}-${draft.srTo}`)}`;
  }
  if (ruleType === "list") {
    return `list-${normalizeCatalogueKey(normalizeSrList(draft.srNumbers).join("-")).slice(0, 90)}`;
  }
  return `single-${fabricGroupingKey(normalizeSrNumber(draft.srNumber))}`;
};

export const buildSrPriceRulePayload = ({
  draft = {},
  brand = {},
  catalogue = {},
} = {}) => {
  const ruleType = draft.ruleType || "single";
  const srNumbers = normalizeSrList(draft.srNumbers);
  return {
    brandId: brand.id || "",
    brandName: brand.name || "",
    catalogueId: catalogue.id || "",
    catalogueName: catalogue.catalogueName || "",
    catalogueKey: catalogue.catalogueKey || "",
    ruleType,
    srNumber: ruleType === "single" ? normalizeSrNumber(draft.srNumber) : "",
    srFrom: ruleType === "range" ? normalizeSrNumber(draft.srFrom) : "",
    srTo: ruleType === "range" ? normalizeSrNumber(draft.srTo) : "",
    srNumbers: ruleType === "list" ? srNumbers : [],
    normalizedSrNumbers: ruleType === "list" ? srNumbers : [],
    rrpPerMeter: toNumberOrNull(draft.rrpPerMeter),
    fabricWidthIn: normalizeFabricWidthIn(draft.fabricWidthIn),
    fabricWidthSource: normalizeFabricWidthIn(draft.fabricWidthIn)
      ? "catalogue_rule"
      : "",
    currency: draft.currency || "INR",
    active: true,
    archived: false,
    notes: toText(draft.notes || ""),
  };
};
